import { FormWidgetProps, InvoiceSummaryRawData, useCatalogItems, useCustomers } from '@melio/ar-domain';
import { Form, Group, LoadingContainer, useWatch } from '@melio/penny';
import { forwardRef, useDebounce } from '@melio/platform-utils';
import { useEffect } from 'react';

import { DiscountProps, InvoiceFormMode, ServiceChargesProps, TaxRateProps } from '../types';
import {
  IncomingDefaultValues,
  InvoiceFormContext,
  InvoiceFormState,
  InvoiceFormValues,
  useInvoiceForm,
} from '../utils';
import { InvoiceDetailsFormSection, InvoiceItemsFormSection, PayOptionsFormSection } from './form-sections';

export type InvoiceFormProps = Pick<
  FormWidgetProps<InvoiceFormValues>,
  'isDisabled' | 'isSaving' | 'onSubmit' | 'onSubmissionStateChange'
> & {
  defaultValues?: IncomingDefaultValues;
  taxRate: TaxRateProps;
  discount: DiscountProps;
  serviceCharges: ServiceChargesProps;
  isLoading?: boolean;
  isLoadingSummary?: boolean;
  error?: PlatformError;
  invoiceSummary?: InvoiceSummaryRawData;
  onLineItemsChange?: (lineItems: InvoiceFormValues['lineItems']) => unknown;
  onFormStateChange?: (state: InvoiceFormState) => void;
  mode?: InvoiceFormMode;
};

export const InvoiceForm = forwardRef<InvoiceFormProps>(
  (
    {
      onSubmit,
      defaultValues,
      isDisabled,
      isSaving,
      isLoading,
      isLoadingSummary,
      discount,
      taxRate,
      serviceCharges,
      onSubmissionStateChange,
      error,
      invoiceSummary,
      onLineItemsChange,
      onFormStateChange,
      mode,
      ...props
    },
    ref
  ) => {
    const { data: catalogItems, isLoading: isLoadingCatalogItems } = useCatalogItems();
    const { isLoading: isLoadingCustomers } = useCustomers();

    const invoiceForm = useInvoiceForm({ onSubmit, defaultValues, isSaving, onSubmissionStateChange });
    const lineItems = useDebounce(useWatch({ control: invoiceForm.control, name: 'lineItems' }), 50);
    useEffect(() => {
      onLineItemsChange?.(lineItems);
    }, [lineItems]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      onFormStateChange?.(invoiceForm.formState);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceForm.formState]);

    return (
      <InvoiceFormContext.Provider value={invoiceForm}>
        <LoadingContainer
          isLoading={isLoadingCatalogItems || isLoadingCustomers}
          data-component={InvoiceForm.displayName}
          ref={ref}
          {...props}
        >
          <Form
            size="small"
            {...invoiceForm.formProps}
            isDisabled={isDisabled}
            isLoading={isLoading}
            error={error && { title: error.code && `Error ${error.code}`, description: error.message }}
          >
            <Group variant="vertical" spacing="xl">
              <InvoiceDetailsFormSection mode={mode} />
              <InvoiceItemsFormSection
                isDisabled={isDisabled}
                isLoading={isLoading}
                isLoadingSummary={isLoadingSummary}
                isSaving={isSaving}
                discount={discount}
                taxRate={taxRate}
                catalogItems={catalogItems}
                serviceCharges={serviceCharges}
                invoiceSummary={invoiceSummary}
                mode={mode}
              />
              <PayOptionsFormSection />
            </Group>
          </Form>
        </LoadingContainer>
      </InvoiceFormContext.Provider>
    );
  }
);
InvoiceForm.displayName = 'InvoiceForm';
